import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { CheckCircle2, Star, ArrowRight, Quote, Send } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";
import { Button } from "./ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import PropertyCard from "./PropertyCard";

const Wrap = ({ children, className = "" }) => <section className={`py-14 ${className}`}><div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div></section>;

function PropertiesBlock({ s }) {
  const params = { limit: s.limit || 4 };
  if (s.mode && s.mode !== "all") params[s.mode] = true;
  const { data } = useQuery({ queryKey: ["page-props", s], queryFn: async () => (await api.get("/properties", { params })).data });
  if (!data?.items?.length) return null;
  return (
    <Wrap>
      {s.heading && <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-6 text-center">{s.heading}</h2>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">{data.items.map((p) => <PropertyCard key={p.id} p={p} />)}</div>
    </Wrap>
  );
}

function ContactBlock({ s }) {
  const [f, setF] = useState({ name: "", mobile: "", requirement: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.mobile) return toast.error("Name and mobile required");
    try { await api.post("/leads", { ...f, source: "landing_page" }); toast.success("Thanks! We'll reach out shortly."); setF({ name: "", mobile: "", requirement: "" }); }
    catch { toast.error("Failed"); }
  };
  return (
    <Wrap className="bg-slate-50">
      <div className="max-w-lg mx-auto text-center">
        {s.heading && <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{s.heading}</h2>}
        {s.subheading && <p className="text-slate-500 mt-2">{s.subheading}</p>}
        <form onSubmit={submit} className="mt-6 space-y-3 text-left bg-white rounded-2xl border border-slate-200 p-6">
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your Name *" className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm" data-testid="lp-contact-name" />
          <input value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} placeholder="Mobile *" className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm" data-testid="lp-contact-mobile" />
          <textarea value={f.requirement} onChange={(e) => setF({ ...f, requirement: e.target.value })} placeholder="What are you looking for?" rows={3} className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm" />
          <Button type="submit" className="w-full bg-indigo-900 hover:bg-indigo-800"><Send className="w-4 h-4 mr-2" /> Submit</Button>
        </form>
      </div>
    </Wrap>
  );
}

export default function SectionRenderer({ section: s }) {
  switch (s.type) {
    case "hero":
      return (
        <section className="relative overflow-hidden">
          {s.image && <div className="absolute inset-0"><img src={fileUrl(s.image)} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-slate-900/55" /></div>}
          <div className={`relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 ${s.image ? "text-white" : ""}`}>
            <div className="max-w-2xl">
              <h1 className="font-heading text-3xl sm:text-5xl font-extrabold leading-tight">{s.heading}</h1>
              {s.subheading && <p className={`mt-4 text-lg ${s.image ? "text-white/90" : "text-slate-600"}`}>{s.subheading}</p>}
              {s.cta_text && <Link to={s.cta_link || "/properties"} className="inline-block mt-7"><Button className="bg-indigo-700 hover:bg-indigo-600 rounded-xl h-12 px-7 text-base">{s.cta_text}</Button></Link>}
            </div>
          </div>
        </section>
      );
    case "features":
      return (
        <Wrap>
          {s.heading && <div className="text-center mb-10"><h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{s.heading}</h2>{s.subheading && <p className="text-slate-500 mt-2">{s.subheading}</p>}</div>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(s.items || []).map((it, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-800 flex items-center justify-center"><Star className="w-5 h-5" /></div>
                <h3 className="font-heading font-bold text-slate-900 mt-4">{it.title}</h3>
                <p className="text-sm text-slate-500 mt-2">{it.description}</p>
              </div>
            ))}
          </div>
        </Wrap>
      );
    case "stats":
      return (
        <Wrap className="bg-indigo-900 text-white">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {(s.items || []).map((it, i) => (
              <div key={i}><div className="font-heading text-3xl sm:text-4xl font-extrabold">{it.value}</div><div className="text-indigo-100/80 text-sm mt-1">{it.label}</div></div>
            ))}
          </div>
        </Wrap>
      );
    case "gallery":
      return (
        <Wrap>
          {s.heading && <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-6 text-center">{s.heading}</h2>}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {(s.images || []).map((img, i) => <img key={i} src={fileUrl(img)} alt="" className="w-full aspect-[4/3] object-cover rounded-xl bg-slate-100" />)}
          </div>
        </Wrap>
      );
    case "testimonials":
      return (
        <Wrap className="bg-slate-50">
          {s.heading && <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-8 text-center">{s.heading}</h2>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(s.items || []).map((it, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6">
                <Quote className="w-6 h-6 text-indigo-300" />
                <p className="text-slate-600 mt-3 text-sm leading-relaxed">"{it.quote}"</p>
                <div className="mt-4 font-heading font-bold text-slate-900 text-sm">{it.name}</div>
                {it.role && <div className="text-xs text-slate-400">{it.role}</div>}
              </div>
            ))}
          </div>
        </Wrap>
      );
    case "faq":
      return (
        <Wrap>
          {s.heading && <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-6 text-center">{s.heading}</h2>}
          <div className="max-w-2xl mx-auto"><Accordion type="single" collapsible>
            {(s.items || []).map((it, i) => (
              <AccordionItem key={i} value={`f${i}`}><AccordionTrigger className="text-left">{it.question}</AccordionTrigger><AccordionContent>{it.answer}</AccordionContent></AccordionItem>
            ))}
          </Accordion></div>
        </Wrap>
      );
    case "text":
      return (
        <Wrap>
          {s.heading && <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-4">{s.heading}</h2>}
          <article className="blog-content max-w-3xl" dangerouslySetInnerHTML={{ __html: s.body_html || "" }} />
        </Wrap>
      );
    case "cta":
      return (
        <Wrap>
          <div className="bg-indigo-900 text-white rounded-3xl p-10 text-center">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold">{s.heading}</h2>
            {s.subheading && <p className="text-indigo-100/90 mt-2">{s.subheading}</p>}
            {s.button_text && <Link to={s.button_link || "/contact"} className="inline-block mt-6"><Button className="bg-white text-indigo-900 hover:bg-indigo-50">{s.button_text}</Button></Link>}
          </div>
        </Wrap>
      );
    case "image":
      return <Wrap><figure className="text-center">{s.image && <img src={fileUrl(s.image)} alt={s.caption || ""} className="w-full rounded-2xl" />}{s.caption && <figcaption className="text-sm text-slate-400 mt-2">{s.caption}</figcaption>}</figure></Wrap>;
    case "properties":
      return <PropertiesBlock s={s} />;
    case "contact":
      return <ContactBlock s={s} />;
    default:
      return null;
  }
}
